import React from 'react';

import {
	defaultSorter,
	makeNextSplitSlides
} from '../helper/Brood.js';

import {
	broncoURLENV,
	monarchServerENV
} from '../ENV';

export const makeFeeds = (props, monarchConfig) => {
	const processedMonarchConfig = monarchConfig.map((feed) => {
		const processedFeed = {...feed};
		processedFeed.name = `monarchfeed${feed.name.replaceAll('_', '')}`;
		processedFeed.src = `${monarchServerENV}/monarch/data${feed.server.path}?tzo=-3`;
		return processedFeed;
	});

	const feeds = (props.condor_prop_list || []).map((child) => {
		const feed = processedMonarchConfig.find((entry) => {
			return entry.name === child.condor_render_name.toLowerCase();
		});

		feed.allow_audio = !!child?.allow_audio || false;

		// Proxied media fallback (DEV-11759): ask monarch for TouchSource
		// /monarch/cache/ URIs instead of direct Screenfeed URLs — for
		// networks that have not approved the Screenfeed domains. The
		// setting arrives as a top-level prop on the skeleton path and as a
		// child prop on the VCL infotainment path.
		if (props.proxied || child.proxied) {
			feed.src += '&proxied=1';
			feed.proxied = true;
		}

		if (props.monarch_location && !child.monarch_location) {
			feed.src += `&location=${props.monarch_location}`;
			feed.location = props.monarch_location;
		}
		if (child.monarch_location) {
			feed.src += `&location=${child.monarch_location}`;
			feed.location = child.monarch_location;
		}

		if (child.monarch__category) {
			feed.src += `&category=${child.monarch__category}`;
			feed.category = child.monarch__category;
		}

		if (child.monarch_distance) {
			feed.src += `&distance=${child.monarch_distance}`;
			feed.distance = child.monarch_distance;
		}

		if (child.monarch_league) {
			feed.src += `&league=${child.monarch_league}`;
			feed.league = child.monarch_league;
		}

		if (child.monarch_order) {
			feed.src += `&order=${child.monarch_order}`;
			feed.order = child.monarch_order;
		}

		if (child.monarch_take) {
			feed.src += `&take=${child.monarch_take}`;
			feed.take = child.monarch_take;
		}

		if (child.monarch_start_date) {
			feed.src += `&start_date=${child.monarch_start_date}`;
			feed.start_date = child.monarch_start_date;
		}

		if (child.monarch_stop_date) {
			feed.src += `&stop_date=${child.monarch_stop_date}`;
			feed.stop_date = child.monarch_stop_date;
		}

		if (child.monarch_sort) {
			feed.src += `&sort=${child.monarch_sort}`;
			feed.sort = child.monarch_sort;
		}

		if (child.monarch_duration) {
			feed.src += `&duration=${child.monarch_duration}`;
			feed.duration = child.monarch_duration;
		}

		if (child.monarch_width) {
			feed.src += `&width=${child.monarch_width}`;
			feed.width = child.monarch_width;
		}

		if (child.monarch_height) {
			feed.src += `&height=${child.monarch_height}`;
			feed.height = child.monarch_height;
		}

		if (child.monarch_size) {
			feed.src += `&size=${child.monarch_size}`;
			feed.size = child.monarch_size;
		}

		if (child.monarch_limit) {
			feed.limit = child.monarch_limit;
		}

		if (child.monarch_theme) {
			feed.src += `&theme=${child.monarch_theme}`;
			feed.theme = child.monarch_theme;
		}

		return feed;
	});

	return feeds;
};

class MonarchProviderComponent extends React.Component {
	static providerName = 'monarch';

	constructor(props) {
		super(props);

		this.state = {
			config:    [],
			slidesMap: {}
		};

		this.fetchTimes = {};
		this.fetchTimer = null;

		this.providerID = `PROVIDER_${props.component_name}`;
	}

	componentDidMount() {
		const {
			updateSlideshowProvider
		} = this.props;

		// don't initialize if we're not in a slideshow
		if (!updateSlideshowProvider) {
			return;
		}

		this.abortController = new AbortController();
		this.fetchConfig();

		this.fetchTimer = setInterval(this.fetchFeeds.bind(this), 1000 * 60);
	}

	componentWillUnmount() {
		const {
			providerID,
			removeSlideshowProvider
		} = this.props;

		if (this.abortController) {
			this.abortController.abort();
		}

		// slidesMap is keyed by feed name; providers were registered under
		// `${providerID}_${feedName}` (makeProvider), so remove by that key
		for (const feedName of Object.keys(this.state.slidesMap)) {
			removeSlideshowProvider(`${providerID}_${feedName}`);
		}

		clearInterval(this.fetchTimer);
	}

	componentDidUpdate(prevProps) {
		const {
			last_update,
			provider
		} = this.props;

		if (prevProps.last_update !== last_update || prevProps.provider !== provider) {
			this.fetchConfig();
		}
	}

	async fetchConfig() {
		const {
			order
		} = this.props;

		try {
			const fetchURL = `${broncoURLENV}/monarch/config`;
			const response = await fetch(fetchURL, {
				signal: this.abortController?.signal
			});

			const monarchConfig = await response.json();

			this.setState({
				config: monarchConfig
			}, () => {
				this.fetchTimes = {};
				this.fetchFeeds();
			});
		} catch (err) {
			if (err.name === 'AbortError') {
				return;
			}
			console.error('BROOD::MONARCH::ERROR_fetchConfig:', err);
		}
	}

	async fetchFeeds() {
		const {
			updateSlideshowProvider
		} = this.props;

		const {
			config,
			slidesMap
		} = this.state;

		try {
			const feeds = makeFeeds(this.props, config);

			// monarch feed ordering
			feeds.sort(defaultSorter);

			const newSlidesMap = {};
			for (const feed of feeds) {
				if (this.fetchTimeTooEarly(feed)) {
					console.log('SKIP(fetchTimeTooEarly):', feed.name, this.fetchTimes[feed.name]);

					continue;
				}

				let feedSlides = await this.fetchFeed(feed);
				if (feedSlides === false) {
					// the feed answered retry — fetchFeed reset its fetchTime so
					// the next 60s cycle refetches; stamping Date.now() below
					// would throttle it for the full 2 * cache.life window
					continue;
				}
				if (!feedSlides) {
					feedSlides = [];
				}

				// add any feed specific data to each slide
				feedSlides = feedSlides.map((slide) => {
					slide.feed = feed;
					slide.allowAudio = feed.allow_audio;

					return slide;
				});
				// newSlidesMap[feed.name] = [...feedSlides];

				newSlidesMap[feed.name] = [];
				for (let i = 0; i < feedSlides.length; i++) {
					const slide = feedSlides[i];
					newSlidesMap[feed.name].push(slide);
				}

				this.fetchTimes[feed.name] = Date.now();
			}

			if (Object.keys(newSlidesMap).length === 0) {
				return;
			}

			const newProviders = [];
			for (let i = 0; i < feeds.length; i++) {
				const feed = feeds[i];
				if (!newSlidesMap[feed.name]) {
					continue;
				}

				const feedSlides = [...newSlidesMap[feed.name]];
				const newProvider = this.makeProvider(feed.name, feedSlides);
				newProviders.push(newProvider);
			}

			this.setState({
				slidesMap: {
					...slidesMap,
					...newSlidesMap
				}
			}, () => {
				// monarch is sorted by sequential/random per monarch/feed
				// slides.sort(defaultSorter);

				updateSlideshowProvider(...newProviders);
			});
		} catch (err) {
			if (err.name === 'AbortError') {
				return;
			}
			console.error('BROOD::MONARCH::ERROR_fetchFeeds:', err);
		}
	}

	fetchTimeTooEarly(feed) {
		const now = Date.now();
		const lastFetchTime = this.fetchTimes[feed.name] || -1;
		if (lastFetchTime === -1) {
			return false;
		}

		const timeSinceLastFetch = now - lastFetchTime;
		const nextFetchTime = (feed.cache.life * 60 * 1000) * 2;
		// const nextFetchTime = 15000;

		if (timeSinceLastFetch >= nextFetchTime) {
			return false;
		}

		return true;
	}

	async fetchFeed(feed) {
		const slides = [];

		try {
			const feedRes = await fetch(feed.src, {
				signal: this.abortController?.signal
			});
			const feedData = await feedRes.json();

			if (feedData.retry) {
				this.fetchTimes[feed.name] = -1;
				return false;
			}

			for (const item of feedData.data) {
				const slide = this.makeSlide(feed, item);
				slides.push(slide);
			}

		} catch (err) {
			if (err.name === 'AbortError') {
				return slides;
			}
			console.error('BROOD::MONARCH::ERROR_fetchFeed:', err);
		}

		return slides;
	}

	makeSlide(feed, item) {
		const {
			providerID
		} = this.props;

		// Absolute http(s) URIs (e.g. direct Screenfeed links) bypass the legacy /monarch/cache wrapper.
		const isAbsoluteURI = /^https?:\/\//i.test(item.uri);
		const src = isAbsoluteURI
			? item.uri
			: `${monarchServerENV}/monarch/cache/${item.uri}?tzo=-3`;

		const slide = {
			feedName: feed.name,
			data:     {
				src,
				alt: item.title,
				convertFormat: false
			},
			options: {
				duration: (feed.duration * 1000)
			},
			slideID:  `${providerID}_${item.uri}`,
			type:     item.media_type,
			provider: this
		};

		return slide;
	}

	makeProvider(feedName = '', slides = []) {
		const {
			provider = {},
			providerID,
			limit: maxSlides,
			order: providerOrder,
			sort: sortType,
			transition_in: transitionIn,
			transition_out: transitionOut,
			isTouch
		} = this.props;

		// Allow autoplay in chrome
		// let isMuted = ~window.location.href.indexOf('muted=1');

		return {
			eventID:    providerID,
			providerID: `${providerID}_${feedName}`,
			providerName: this.constructor.providerName, // Add the static providerName to the provider object
			options:    {
				maxSlides:     maxSlides,
				providerOrder: providerOrder,
				sortType:      sortType || 'sequential'
			},
			is_backfill:       provider.is_backfill,
			slots:             provider.slots,
			order:             provider.order,
			skipper:           this.skipper.bind(this),
			slides,
		};
	}

	// TODO: when this is implemented skipped/nonSkipped slides will
	// TODO: need to be tracked/merged just like in Pelican.js
	slideExpired(currSlide) {
		return false;
	}

	skipper(currSlide) {
		if (this.slideExpired(currSlide)) {
			return true;
		}

		return false;
	}

	render() {
		return null;
	}
}

export default MonarchProviderComponent;